import * as React from 'react'
import * as API from '../lib/api'
import { PurchaseOption } from '../lib/purchase_option'
import { TaxInformation } from '../lib/tax_information'

interface IProps {
  purchaseOptions: PurchaseOption[]
  selectedPurchaseOptionName: API.PurchaseOptionNameType
  onPurchaseOptionChange: (name: API.PurchaseOptionNameType) => void
  taxInformation: TaxInformation
  trial: boolean
}

const KEEP_BENEFIT_HEADLINE = 'Keep what you download'
const KEEP_BENEFIT_DETAIL = 'Every file you download is yours to keep — even if you cancel.'

export class OrderDetails extends React.Component<IProps, object> {
  public render() {
    const option = this.selectedOption()

    // A transient name/options mismatch during a toggle shouldn't crash the
    // rail; render nothing so the subtree recovers on the next props update.
    if (!option) {
      return null
    }

    return (
      <section className="order-details" data-testid="order-details">
        {this.renderHeader(option)}
        {this.renderKeepBenefit()}
        {this.renderPrices(option)}
        {this.renderTotal(option)}
        {this.renderTaxNote()}
      </section>
    )
  }

  protected renderHeader(option: PurchaseOption) {
    const { membershipPlan } = option

    return (
      <header className="order-details__header">
        <p className="order-details__kicker" data-testid="order-details-kicker">Order Details</p>
        <div className="order-details__title-row">
          <h2 className="order-details__plan-name">{membershipPlan.name}</h2>
          {this.renderToggle()}
        </div>
        <p className="order-details__plan-subtitle" data-testid="order-details-plan-subtitle">
          {this.planSubtitle(option)}
        </p>
      </header>
    )
  }

  // Uses the same undiscounted per-payment basis as `afterTrialDescription`
  // (`fullPrice()`) so the two figures agree.
  protected planSubtitle(option: PurchaseOption): string {
    const perPayment = this.formatPrice(option.fullPrice())

    if (option.name === 'annual') {
      return `Annual payment of $${perPayment}`
    }

    return `${option.paymentDetails.numberOfPayments} installments of $${perPayment}/month`
  }

  // Keeps radiogroup semantics rather than `role="switch"`: a switch announces
  // "on/off", which can't convey "Monthly vs Annual", and the radiogroup
  // generalizes past two cadences. The sliding knob is decorative — its side is
  // driven by the CSS `--knob-*` modifier below, not a separate control.
  protected renderToggle() {
    const { purchaseOptions, selectedPurchaseOptionName } = this.props
    const isSwitch = purchaseOptions.length === 2

    // Keyed off the selected label's rendered position, not its cadence name:
    // the host decides the order, so a name-based rule would put the knob on the
    // wrong side whenever the order isn't Monthly-then-Annual.
    const selectedIndex = purchaseOptions.findIndex((o) => o.name === selectedPurchaseOptionName)
    const knobSide = selectedIndex >= 1 ? 'end' : 'start'

    return (
      <div
        className={`order-details__toggle order-details__toggle--knob-${knobSide}`}
        role="radiogroup"
        aria-label="Billing cadence"
      >
        {purchaseOptions.map((option, index) => {
          const label = this.renderToggleOption(option, index)

          // The track is a mouse affordance layered over the real radios (which
          // stay the keyboard/AT interface), so it is aria-hidden.
          if (isSwitch && index === 1) {
            return (
              <React.Fragment key={option.name}>
                <span
                  className="order-details__toggle-track"
                  data-testid="order-details-toggle-track"
                  aria-hidden="true"
                  onClick={this.handleToggle}
                >
                  <span className="order-details__toggle-knob" />
                </span>
                {label}
              </React.Fragment>
            )
          }

          return label
        })}
      </div>
    )
  }

  protected renderToggleOption(option: PurchaseOption, index: number) {
    const active = option.name === this.props.selectedPurchaseOptionName

    return (
      <button
        key={option.name}
        ref={(el) => { this.optionRefs[option.name] = el }}
        type="button"
        role="radio"
        className={`order-details__toggle-option${active ? ' order-details__toggle-option--active' : ''}`}
        data-testid={`order-details-option-${option.name}`}
        aria-checked={active}
        tabIndex={active ? 0 : -1}
        onClick={() => this.handleSelect(option.name)}
        onKeyDown={(event) => this.handleArrow(event, index)}
      >
        {option.label}
      </button>
    )
  }

  // Keyed by option name so an arrow key can focus the newly-selected radio
  // (roving-tabindex pattern).
  protected optionRefs: { [name: string]: HTMLButtonElement | null } = {}

  protected handleArrow(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown'
    const backward = event.key === 'ArrowLeft' || event.key === 'ArrowUp'

    if (!forward && !backward) {
      return
    }

    event.preventDefault()

    const { purchaseOptions } = this.props
    const count = purchaseOptions.length
    const delta = forward ? 1 : -1
    const next = purchaseOptions[(index + delta + count) % count]

    this.handleSelect(next.name)
    const button = this.optionRefs[next.name]
    if (button) {
      button.focus()
    }
  }

  protected renderKeepBenefit() {
    return (
      <p className="order-details__keep-benefit" data-testid="order-details-keep-benefit">
        <strong className="order-details__keep-benefit-headline">{KEEP_BENEFIT_HEADLINE}</strong>{' '}
        <span className="order-details__keep-benefit-detail">{KEEP_BENEFIT_DETAIL}</span>
      </p>
    )
  }

  // Subtotal is omitted when no discount applies — it would just repeat Plan
  // price verbatim, and two identical lines read as a bug.
  protected renderPrices(option: PurchaseOption) {
    const { paymentDetails } = option
    const discounted = paymentDetails.priceBeforeTax !== paymentDetails.basePrice

    return (
      <ul className="order-details__prices" data-testid="order-details-prices">
        {this.priceLine('Plan price', paymentDetails.basePrice)}
        {discounted && this.priceLine('Subtotal', paymentDetails.priceBeforeTax)}
        {this.renderTaxLine()}
      </ul>
    )
  }

  protected renderTaxLine() {
    const { taxInformation } = this.props

    if (!taxInformation.isLoaded) {
      return (
        <li className="order-details__price-line">
          <span className="order-details__price-label">{taxInformation.salesTaxLabel}</span>
          <span className="order-details__price-amount">Calculating&hellip;</span>
        </li>
      )
    }

    return this.priceLine(taxInformation.salesTaxLabel, taxInformation.salesTaxAmount || 0)
  }

  protected renderTotal(option: PurchaseOption) {
    return (
      <div className="order-details__total-block">
        {this.renderAfterTrial(option)}
        <div className="order-details__total" data-testid="order-details-total">
          <span className="order-details__total-label">Total Due Today</span>
          <span className="order-details__total-amount">${this.formatPrice(this.totalDueToday(option))}</span>
        </div>
      </div>
    )
  }

  protected renderAfterTrial(option: PurchaseOption) {
    if (!this.props.trial) {
      return null
    }

    return (
      <p className="order-details__after-trial" data-testid="order-details-after-trial">
        {this.afterTrialDescription(option)}
      </p>
    )
  }

  // Deliberately uses the undiscounted, pre-tax per-payment list price, unlike
  // the control `Cart` which shows the current order's discounted/taxed total:
  // a first-order discount and tax on today's $0 trial charge aren't
  // representative of what recurs once the trial ends.
  protected afterTrialDescription(option: PurchaseOption): string {
    const perPayment = this.formatPrice(option.fullPrice())
    const { numberOfPayments } = option.paymentDetails

    if (option.name === 'annual') {
      return `Annual Payment of $${perPayment} after trial`
    }

    return `${numberOfPayments} Monthly Payments of $${perPayment} after trial`
  }

  // Explains the `*` carried by the Sales Tax line.
  protected renderTaxNote() {
    return (
      <p className="order-details__footnote" data-testid="order-details-tax-note">
        *Tax exempt? Complete checkout, and you can submit your exemption certificate for a sales tax refund.
      </p>
    )
  }

  protected handleSelect(name: API.PurchaseOptionNameType) {
    if (name === this.props.selectedPurchaseOptionName) {
      return
    }
    this.props.onPurchaseOptionChange(name)
  }

  protected handleToggle = () => {
    const { purchaseOptions, selectedPurchaseOptionName } = this.props
    const other = purchaseOptions.find((o) => o.name !== selectedPurchaseOptionName)
    if (other) {
      this.handleSelect(other.name)
    }
  }

  protected priceLine(label: string, amount: number) {
    return (
      <li key={label} className="order-details__price-line">
        <span className="order-details__price-label">{label}</span>
        <span className="order-details__price-amount">${this.formatPrice(amount)}</span>
      </li>
    )
  }

  protected totalDueToday(option: PurchaseOption): number {
    if (this.props.trial) {
      return 0
    }

    return option.paymentDetails.total({ taxInformation: this.props.taxInformation })
  }

  protected selectedOption(): PurchaseOption | undefined {
    const { purchaseOptions, selectedPurchaseOptionName } = this.props

    return purchaseOptions.find((o) => o.name === selectedPurchaseOptionName)
  }

  protected formatPrice(price: number): string {
    return Number(price).toFixed(2)
  }
}
